import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, ExternalLink } from 'lucide-react';
import { POI } from '../../types';
interface POIPopupCardProps {
  poi: POI;
  onClose: () => void;
  position: {
    x: number;
    y: number;
  };
}
// Map category to color styles
const getCategoryStyles = (category: POI['category']) => {
  switch (category) {
    case 'Nature':
      return 'bg-green-500/20 text-green-400 border-green-500/30';
    case 'Dining':
      return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
    case 'Fitness':
      return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
    default:
      return 'bg-green-500/20 text-green-400 border-green-500/30';
  }
};
export function POIPopupCard({
  poi,
  onClose,
  position
}: POIPopupCardProps) {
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);
  const categoryStyle = getCategoryStyles(poi.category);
  return <motion.div
  // Start slightly higher (40px) and animate to final position (60px)
  initial={{
    opacity: 0,
    scale: 0.9,
    y: 40
  }} animate={{
    opacity: 1,
    scale: 1,
    y: 60
  }} exit={{
    opacity: 0,
    scale: 0.9,
    y: 40
  }} transition={{
    type: 'spring',
    damping: 25,
    stiffness: 300
  }} className="absolute z-50 pointer-events-auto" style={{
    left: `${position.x}%`,
    top: `${position.y}%`,
    x: '-50%' // Center horizontally using Framer Motion style
  }} data-id="element-990">
      <div className="relative w-80 bg-slate-900/95 backdrop-blur-xl rounded-xl border border-white/10 shadow-2xl overflow-hidden" data-id="element-991">
        {/* Header Image */}
        <div className="relative h-40 overflow-hidden" data-id="element-992">
          <img src={poi.images[0]} alt={poi.name} className="w-full h-full object-cover" data-id="element-993" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900 to-transparent" data-id="element-994" />

          {/* Close Button */}
          <button onClick={onClose} className="absolute top-3 right-3 p-2 rounded-full bg-slate-900/80 backdrop-blur-sm border border-white/10 text-white hover:bg-slate-800 transition-colors" aria-label="Close" data-id="element-995">
            <X className="w-4 h-4" data-id="element-996" />
          </button>

          {/* Category Badge */}
          <div className="absolute bottom-3 left-3" data-id="element-997">
            <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider border backdrop-blur-sm ${categoryStyle}`} data-id="element-998">
              {poi.category}
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="p-4" data-id="element-999">
          <h3 className="text-lg font-bold text-white mb-2" data-id="element-1000">{poi.name}</h3>
          <p className="text-sm text-slate-300 leading-relaxed mb-3" data-id="element-1001">
            {poi.description}
          </p>

          {/* Website Link */}
          {poi.websiteUrl && <a href={poi.websiteUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm text-cyan-400 hover:text-cyan-300 transition-colors" data-id="element-1002">
              Visit Website
              <ExternalLink className="w-3.5 h-3.5" data-id="element-1003" />
            </a>}
        </div>

        {/* Pointer arrow - pointing up to the icon */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-4 h-4 bg-slate-900/95 border-l border-t border-white/10 rotate-45" data-id="element-1004" />
      </div>
    </motion.div>;
}